import React, { Fragment } from "react";
import { Card as TemplateCard } from "v2/components/Templates";
import TenanciesTab from "components/Projects/Show/TenanciesTab";
import TenantTab from "components/Projects/Show/TenantTab";
import { translate } from "lib";

interface SummaryProps {
	template?: tcpinpoint.Template;
	/**
	 * FLAT IDS, not records — see the note on the data path below.
	 */
	tenancyIds?: (string | number)[];
	tenantId?: string | number;
	propertyId?: string | number;
}

/**
 * Read-only summary of what a PENDING project already is, shown as the first
 * step of the finalize wizard (`/projects/:id/schedule`) before Scheduling.
 *
 * ---------------------------------------------------------------------------
 * IT TAKES IDS AND LETS THE SHARED TABS FETCH (Sim, 28 Aug)
 * ---------------------------------------------------------------------------
 * This step used to receive the `tenancy` / `tenancies` / `tenant` / `lease`
 * RECORDS that `GET /v2/projects/:id?include=[...]` returns alongside the
 * project, and render its own copies of the unit list and the property panel. On
 * an AMENDMENT project every one of them arrived EMPTY, and worth understanding
 * because it is not obvious:
 *
 *   A Yardi project has NO TCP `Lease` (Amendment::Ingestor creates it with an
 *   `amendment` and nothing else), so `Project#tenancies` and `Project#tenant`
 *   are OVERRIDDEN to answer from the ERD instead — the amendment's unit
 *   snapshot, and `lease_agreement.current_tenant`. But jsonapi-serializer does
 *   not build a relationship's `data` from those methods: it asks the record for
 *   `tenancy_ids` / `lease_id`, the readers ActiveRecord generated for the
 *   through-the-lease associations, which never see the override and come back
 *   empty. So `data` was `[]` / `null` — and `lib/jsonapi-normalizer` spreads a
 *   null relationship into `{}`, which is TRUTHY. Hence a nameless unit row, a
 *   tenant card reading only "Unknown", and a lease card of "$0.00".
 *
 * That is exactly why ProjectSerializer carries `tenancy_ids` and `tenant_id` as
 * flat ATTRIBUTES — those DO call the overridden methods — and why the project
 * workspace (`components/Projects/Show`) reads the flat ids and lets each tab
 * fetch its own record. This step now does the same, with the SAME shared
 * components rather than its own lookalikes: `Projects/Show/TenanciesTab` (the
 * unit list plus one shared property panel and map) and
 * `Projects/Show/TenantTab`. One place to fix, one look.
 *
 * NO LEASE SECTION (Sim, 28 Aug). It showed TCP's own `Lease`, and the amendment
 * ERD left that record with four fields — every date and every rent figure read
 * "Unknown" or "$0.00" on a Yardi project. The commercial terms now belong to the
 * AMENDMENT and are shown on the lease itself, under Leases -> the lease. The
 * project workspace dropped its Lease tab on 24 Aug for exactly this reason.
 *
 * Nothing here is interactive: `.summary-step.read-only` (see
 * `scss/components/_wizard.scss`) kills pointer events on inputs, buttons and
 * links, and neither tab is passed `editable` / `onClear`, so no edit or close
 * affordance is rendered. The unit rows stay expandable — their toggle is a
 * `div role="button"`, not a `<button>`.
 */
const Summary = ({ template, tenancyIds, tenantId, propertyId }: SummaryProps) => (
	<div className="summary-step read-only">
		{!!template && (
			<Fragment>
				<h4>{translate("template")}</h4>
				<div className="card">
					{/* TemplateCard's `onUpdate` prop is typed as required even though the
					component itself only renders the close button `if (onUpdate)` — omitting
					it is the documented way to hide the button (react-frontend-tasks F1), so
					the type mismatch is expected and safe to ignore here. */}
					{/* @ts-ignore */}
					<TemplateCard template={template} />
				</div>
			</Fragment>
		)}
		{/* ALWAYS RENDERED, empty or not: TenanciesTab's own empty state says "No
		    Tenancies found", and an amendment that named no units is worth saying
		    out loud — a section that silently disappears reads as broken. */}
		<Fragment>
			<h4>{translate("tenancy")}</h4>
			<div className="card">
				<TenanciesTab tenancyIds={tenancyIds || []} propertyId={propertyId || ""} />
			</div>
		</Fragment>
		{!!tenantId && (
			<Fragment>
				<h4>{translate("tenant")}</h4>
				<div className="card">
					<TenantTab tenantId={tenantId} />
				</div>
			</Fragment>
		)}
	</div>
);

export default Summary;
